import { useLocalStorage } from './useLocalStorage';

export interface MaintenanceProgress {
  [taskId: string]: {
    completed: boolean;
    completedAt?: string;
  };
}

export interface DailyProgress {
  date: string;
  tasks: MaintenanceProgress;
}

export function useMaintenance() {
  const [progress, setProgress] = useLocalStorage<DailyProgress[]>('dtf-wizard-maintenance', []);

  const getTodayKey = () => {
    return new Date().toISOString().split('T')[0];
  };

  const getTodayProgress = (): MaintenanceProgress => {
    const today = getTodayKey();
    const todayEntry = progress.find(p => p.date === today);
    return todayEntry?.tasks || {};
  };

  const toggleTask = (taskId: string) => {
    const today = getTodayKey();
    setProgress(prev => {
      const existingIndex = prev.findIndex(p => p.date === today);
      const todayProgress = existingIndex >= 0 ? prev[existingIndex].tasks : {};
      
      const newProgress = {
        ...todayProgress,
        [taskId]: {
          completed: !todayProgress[taskId]?.completed,
          completedAt: !todayProgress[taskId]?.completed ? new Date().toISOString() : undefined
        }
      };

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = { date: today, tasks: newProgress };
        return updated;
      }
      return [...prev, { date: today, tasks: newProgress }];
    });
  };

  const isTaskCompleted = (taskId: string): boolean => {
    const todayProgress = getTodayProgress();
    return todayProgress[taskId]?.completed || false;
  };

  const getCompletionRate = (totalTasks: number): number => {
    const todayProgress = getTodayProgress();
    const completed = Object.values(todayProgress).filter(t => t.completed).length;
    return totalTasks > 0 ? Math.round((completed / totalTasks) * 100) : 0;
  };

  const resetToday = () => {
    const today = getTodayKey();
    setProgress(prev => prev.filter(p => p.date !== today));
  };

  const exportProgress = (): string => {
    return JSON.stringify(progress, null, 2);
  };

  return {
    progress,
    getTodayProgress,
    toggleTask,
    isTaskCompleted,
    getCompletionRate,
    resetToday,
    exportProgress
  };
}
